import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ShareProductButton from "../../components/ecommerce/ShareProductButton";
import WhatsAppProductButton from "../../components/ecommerce/WhatsAppProductButton";
import ProductImageGallery from "../../components/ecommerce/ProductImageGallery";
import AddToCartButton from "../../components/ecommerce/AddToCartButton";
import ProductCard from "../../components/ecommerce/ProductCard";
import ProductPriceDisplay from "../../components/ecommerce/ProductPriceDisplay";
import { getProductShareUrl } from "../../utils/ecommerce/share-product";
import { getProductPricing, getPriceLocale, formatProductPrice } from "../../utils/ecommerce/product-pricing";
import { buildProductGalleryImages } from "../../utils/ecommerce/product-gallery-utils";
import { isUserLoggedIn } from "../../utils/ecommerce/auth-session";
import { EC } from "../../utils/const/ecommerce-colors-const";
import { obtenerProductoXId, obtenerListaProductosPublicos } from "../../api/api-producto";
import { sharedApiErrorMessage } from "../../utils/shared-functions";
import { scrollToTop } from "../../utils/ecommerce/scroll-to-top";
import ProductDetailSkeleton from "../../utils/ecommerce/ProductDetailSkeleton";

export default function DetalleProductoPage() {
    const { id: idProducto } = useParams();
    const history = useHistory();
    const { t, i18n } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [product, setProduct] = useState(null);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [loggedIn, setLoggedIn] = useState(false);

    useEffect(() => {
        scrollToTop();
        setLoggedIn(isUserLoggedIn());
    }, [idProducto]);

    useEffect(() => {
        let cancelled = false;

        const loadProduct = async () => {
            setLoading(true);
            setLoadError(null);
            setProduct(null);
            setRelatedProducts([]);

            try {
                const response = await obtenerProductoXId({ idProducto: idProducto }, t);

                if (cancelled) return;

                if (response.error) {
                    setLoadError(sharedApiErrorMessage(response.message, t("ct_product_not_found")));
                    return;
                }

                const producto = response.body;
                if (!producto) {
                    setLoadError(t("ct_product_not_found"));
                    return;
                }

                setProduct(producto);
                document.title = producto.nombre ? `${producto.nombre} | ComprarTodo` : "ComprarTodo";

                const relatedResponse = await obtenerListaProductosPublicos({}, t);
                if (!cancelled && relatedResponse.error === false) {
                    const list = Array.isArray(relatedResponse.body) ? relatedResponse.body : [];
                    setRelatedProducts(
                        list
                            .filter(
                                (item) =>
                                    item.id !== producto.id &&
                                    item.idCategoriaProducto === producto.idCategoriaProducto
                            )
                            .slice(0, 4)
                    );
                }
            } catch (err) {
                if (!cancelled) {
                    setLoadError(sharedApiErrorMessage(err, t("alert_process_failed")));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        if (idProducto) {
            loadProduct();
        } else {
            setLoading(false);
            setLoadError(t("ct_product_not_found"));
        }

        return () => {
            cancelled = true;
        };
    }, [idProducto, t]);

    if (loading) {
        return <ProductDetailSkeleton />;
    }

    if (loadError || !product) {
        return (
            <div className={`min-h-screen flex flex-col items-center justify-center px-4 text-center ${EC.page.root}`}>
                <p className="text-6xl mb-4" aria-hidden>🔍</p>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">{t("ct_product_not_found")}</h1>
                <p className="text-slate-600 mb-6">{loadError || t("ct_product_not_found_desc")}</p>
                <button
                    type="button"
                    onClick={() => history.push("/landing")}
                    className={`rounded-xl px-6 py-3 ${EC.btn.primary}`}
                >
                    {t("label_volver_al_inicio")}
                </button>
            </div>
        );
    }

    const name = product.nombre;
    const description = product.descripcion;
    const categoryLabel = product.categoriaProducto ? t(`${product.categoriaProducto}`) : "";
    const galleryImages = buildProductGalleryImages(product);
    const locale = getPriceLocale(i18n.language);
    const pricing = getProductPricing(product);

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900">
            <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-md">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
                    <button
                        type="button"
                        onClick={() => history.push("/landing")}
                        className={`flex items-center gap-2 font-bold transition ${EC.brand.text}`}
                    >
                        <span aria-hidden>←</span>
                        <span className="hidden sm:inline">{t("label_volver_al_inicio")}</span>
                        <span className="sm:hidden">{t("ct_back")}</span>
                    </button>
                    <span className="font-extrabold text-base sm:text-lg text-slate-900 truncate max-w-[30vw] sm:max-w-none text-center">
                        🛒 {t("ct_brand")}
                    </span>
                    <ShareProductButton productId={product.id} productName={name} />
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                    <ProductImageGallery images={galleryImages} alt={name} />

                    <div>
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                            {categoryLabel && (
                                <span className={EC.chip.categoryLg}>{categoryLabel}</span>
                            )}
                            {product.badge && (
                                <span
                                    className={`rounded-full px-3 py-1 text-xs font-bold uppercase text-white ${product.badge === "new" ? EC.badge.new : EC.badge.hot
                                        }`}
                                >
                                    {t(`${product.badge}`)}
                                </span>
                            )}
                            {pricing.isOnSale && (
                                <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase text-white ${EC.badge.sale}`}>
                                    {t("ct_discount_off", { percent: pricing.porcentajeDescuento })}
                                </span>
                            )}
                        </div>

                        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-3">
                            {name}
                        </h1>

                        {product.rating && (
                            <p className={`flex items-center gap-1 font-semibold mb-4 ${EC.rating.text}`}>
                                ★ {product.rating}{" "}
                                <span className="text-slate-400 font-normal text-sm">/ 5 · {t("ct_trusted_rating")}</span>
                            </p>
                        )}

                        <div className={`mb-6 ${EC.price.box}`}>
                            <p className={`mb-1 ${EC.price.label}`}>
                                {pricing.isOnSale ? t("ct_price_sale_label") : t("ct_price_label")}
                            </p>
                            <ProductPriceDisplay product={product} size="lg" />
                            {pricing.isOnSale && (
                                <p className="mt-2 text-sm font-medium text-ct-price-600">
                                    {t("ct_you_save")}{" "}
                                    {formatProductPrice(
                                        pricing.precioOriginal - pricing.salePrice,
                                        pricing.abreviatura,
                                        locale
                                    )}
                                </p>
                            )}
                        </div>

                        <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-8">{description}</p>

                        {!loggedIn && (
                            <div className={`mb-4 ${EC.alert.box}`}>{t("ct_login_to_buy_hint")}</div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-3 mb-8">
                            {/* <AddToCartButton product={product} /> */}
                            <WhatsAppProductButton
                                telefonoEmpresa={product.telefonoEmpresa}
                                productId={product.id}
                                productName={name}
                            />
                            <ShareProductButton productId={product.id} productName={name} className="sm:shrink-0" />
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                            <p className="text-sm font-semibold text-slate-700 mb-2">{t("ct_share_link_label")}</p>
                            <p className="break-all text-xs sm:text-sm font-mono text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                                {getProductShareUrl(product.id)}
                            </p>
                            <p className="text-xs text-slate-400 mt-2">{t("ct_share_link_hint")}</p>
                        </div>
                    </div>
                </div>

                {relatedProducts.length > 0 && (
                    <section className="mt-16 md:mt-20">
                        <h2 className="text-2xl font-bold text-slate-900 mb-6">{t("ct_related_products")}</h2>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 sm:gap-5">
                            {relatedProducts.map((item) => (
                                <ProductCard key={item.id} product={item} compact />
                            ))}
                        </div>
                    </section>
                )}
            </main>
        </div>
    );
}
